import { VariantUpdatedEvent, VariantSelectedEvent } from '@theme/events';
// import { morph } from '@theme/morph';

/*
 * Hairlyne : à l'arrivée sur la page, aucune couleur n'est sélectionnée.
 * Le client doit cliquer sur une couleur (sa photo s'affiche alors comme d'habitude).
 * Tant qu'aucune couleur n'est choisie, l'ajout au panier est bloqué et l'étape couleur est signalée.
 */
const HL_STYLE_ID = 'hl-color-pending-style';
if (!document.getElementById(HL_STYLE_ID)) {
  const style = document.createElement('style');
  style.id = HL_STYLE_ID;
  style.textContent = `
    .hl-step--pending .hl-step__selected { font-style: italic; opacity: .7 !important; }
    .hl-step--error { border-color: #c62828 !important; box-shadow: 0 0 0 1px #c62828; }
    .hl-step__error { display: none; margin: 10px 0 0; font-size: 13px; font-weight: 600; color: #c62828; }
    .hl-step--error .hl-step__error { display: block; }
  `;
  document.head.appendChild(style);
}

class VariantPicker extends HTMLElement {
  // Contrôleur pour annuler les requêtes en cours
  #abortController;

  // Hairlyne : le client a-t-il cliqué sur une couleur ?
  #colorChosen = false;
  // Hairlyne : variante correspondant au dernier choix du client
  #lastVariantId = '';
  #guardTarget = null;

  connectedCallback() {
    this.addEventListener('change', this.variantChanged.bind(this));

    this.#applyColorState();

    // Bloque l'ajout au panier tant qu'aucune couleur n'est choisie (écoute en phase de capture, avant le formulaire)
    this.#guardTarget = this.closest('product-form') || this.closest('form');
    this.#guardTarget?.addEventListener('submit', this.#guardSubmit, true);
  }

  disconnectedCallback() {
    this.#guardTarget?.removeEventListener('submit', this.#guardSubmit, true);
  }

  // Étape (fieldset) de l'option couleur
  get #colorFieldset() {
    return Array.from(this.querySelectorAll('fieldset')).find((fieldset) => {
      const input = fieldset.querySelector('input[type="radio"][name^="option-"]');
      return input && /couleur|color/i.test(input.getAttribute('name') || '');
    });
  }

  // Uniquement sur la page produit (pas dans les cartes produit ni l'ajout rapide)
  get #enabled() {
    return (
      (typeof Theme !== 'undefined' ? Theme.template?.name : window.Theme?.template?.name) === 'product' &&
      this.classList.contains('hl-configurator') &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog')
    );
  }

  // Désélectionne la couleur tant que le client ne l'a pas choisie
  #applyColorState() {
    if (this.#colorChosen) return;
    if (!this.#enabled) {
      this.#colorChosen = true;
      return;
    }

    const fieldset = this.#colorFieldset;
    if (!fieldset) {
      this.#colorChosen = true;
      return;
    }

    const checked = fieldset.querySelector('input:checked');
    if (checked) {
      fieldset.dataset.hlDefault = checked.dataset.optionValueId || '';
      checked.checked = false;
      checked.removeAttribute('checked');
    }

    fieldset.classList.add('hl-step--pending');

    const selected = fieldset.querySelector('.hl-step__selected');
    if (selected) selected.textContent = 'À choisir';

    if (!fieldset.querySelector('.hl-step__error')) {
      const error = document.createElement('p');
      error.className = 'hl-step__error';
      error.textContent = 'Choisissez votre couleur pour continuer.';
      fieldset.appendChild(error);
    }

    const optionName = fieldset.querySelector('.hl-step__title')?.textContent.trim().toLowerCase();
    this.querySelectorAll('.hl-summary__list li').forEach((li) => {
      const label = li.querySelector('span')?.textContent.trim().toLowerCase();
      if (label && optionName && label === optionName) {
        const value = li.querySelector('strong');
        if (value) value.textContent = 'À choisir';
      }
    });
  }

  #guardSubmit = (event) => {
    if (this.#colorChosen) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const fieldset = this.#colorFieldset;
    if (fieldset) {
      fieldset.classList.add('hl-step--error');
      fieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  /**
   * Gère l'événement de changement de variante.
   * @param {Event} event - L'événement de changement de variante.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Mémorise la variante correspondant au choix du client
    if (event.target instanceof HTMLSelectElement) {
      this.#lastVariantId = event.target.options[event.target.selectedIndex]?.dataset.variantId || '';
    } else {
      this.#lastVariantId = event.target.dataset.variantId || '';
    }

    // Le client a cliqué sur une couleur : sa photo s'affichera en premier
    const optionName = (event.target.getAttribute('name') || '').toLowerCase();
    if (optionName.includes('couleur') || optionName.includes('color')) {
      this.#colorChosen = true;
      const fieldset = this.#colorFieldset;
      fieldset?.classList.remove('hl-step--pending', 'hl-step--error');
    }

    // Met à jour l'option sélectionnée dans l'interface
    this.updateSelectedOption(event.target);

    // Déclenche un événement personnalisé pour notifier la sélection
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    // Lance la requête pour mettre à jour la section
    this.fetchUpdatedSection(this.buildRequestUrl());

    // Met à jour l'URL avec l'ID de variante sur les pages produit
    this.updateUrl(event.target);
  }

  // Met à jour l'option sélectionnée dans l'interface.
  updateSelectedOption(target) {
    // Si c'est une chaîne, trouve l'élément correspondant pour récupérer l'ID de la valeur d'option
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    // Met à jour l'état "checked" pour les boutons radio
    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    // Met à jour l'attribut "selected" pour les éléments select
    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      // Supprime l'attribut selected de toutes les options
      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      // Ajoute l'attribut selected à la nouvelle option
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  // Construit l'URL de requête pour récupérer les données de variante.
  buildRequestUrl() {
    let productUrl = this.dataset.productUrl;

    // On utilise la nouvelle API de Shopify pour récupérer les données de variante avec option_values.
    const params = [];
    params.push(`option_values=${this.selectedOptionsValues.join(',')}`);

    // Hairlyne : une fois la couleur choisie, on transmet la variante pour que la galerie affiche sa photo en premier
    if (this.#colorChosen && this.#lastVariantId) {
      params.push(`variant=${this.#lastVariantId}`);
    }

    return `${productUrl}?${params.join('&')}`;
  }

  // Récupère la section mise à jour depuis le serveur.
  fetchUpdatedSection(requestUrl) {
    // Annule la requête fetch précédente si elle est encore en cours
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        // Récupère les données JSON de la variante depuis la réponse
        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        // Met à jour seulement le variant picker
        this.updateVariantPicker(html);

        // Déclenche un événement pour notifier la mise à jour de la variante
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdatedEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
            }),
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log("Fetch interrompu par l'utilisateur");
        } else {
          console.error(error);
        }
      });
  }

  // Re-rend le sélecteur de variante avec les nouvelles données.
  updateVariantPicker(newHtml) {
    // Trouve le nouveau variant picker dans la réponse
    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('Aucune nouvelle source de variant picker trouvée');
    }

    // Met à jour le variant picker
    this.innerHTML = newVariantPickerSource.innerHTML;

    // Hairlyne : garde la couleur désélectionnée tant qu'elle n'a pas été choisie
    this.#applyColorState();
  }

  // Met à jour l'URL avec l'ID de variante sur les pages produit
  updateUrl(target) {
    // Détermine si nous sommes sur une page produit (pas dans une carte produit ou dialogue)
    const isOnProductPage =
      Theme.template.name === 'product' && !target.closest('product-card') && !target.closest('quick-add-dialog');

    // Prépare l'URL pour la mise à jour de l'historique du navigateur
    const url = new URL(window.location.href);

    let variantId;

    // Extrait l'ID de la variante selon le type d'élément (radio ou select)
    if (target instanceof HTMLInputElement && target.type === 'radio') {
      variantId = target.dataset.variantId || null;
    } else if (target instanceof HTMLSelectElement) {
      const selectedOption = target.options[target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    // Met à jour l'URL avec l'ID de variante sur les pages produit
    if (isOnProductPage) {
      if (variantId && this.#colorChosen) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Met à jour l'URL dans l'historique du navigateur si elle a changé
    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  // Récupère l'option actuellement sélectionnée.
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  // Récupère l'ID de l'option actuellement sélectionnée.
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("Aucun ID de valeur d'option trouvé");
    }

    return optionValueId;
  }

  // Récupère toutes les valeurs d'options actuellement sélectionnées.
  // Hairlyne : si la couleur n'est pas encore choisie, on utilise sa valeur par défaut pour la requête.
  get selectedOptionsValues() {
    const values = [];

    this.querySelectorAll('select, fieldset').forEach((element) => {
      if (element instanceof HTMLSelectElement) {
        const option = element.querySelector('option[selected]') || element.options[element.selectedIndex];
        if (option?.dataset.optionValueId) values.push(option.dataset.optionValueId);
        return;
      }

      const checked = element.querySelector('input:checked[data-option-value-id]');
      const id = checked?.dataset.optionValueId || element.dataset.hlDefault;
      if (id) values.push(id);
    });

    return values;
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
